import TransactionStatus from "enums/TransactionStatus";
import TransactionsType from "enums/TransactionsType";
import WorkflowRequestStatus from "enums/WorkflowRequestStatus";
import fullName from "utils/fullName";
import { OUTSTANDING_REIMBURSEMENT_STATUSES } from "utils/transactions/transactionGroups";
import type { UserAvatarUser } from "components/common/users/UserAvatar";

/**
 * The reimbursement queue's two tabs; the Status filter narrows within them.
 *
 * The lifecycle: a submitted reimbursement is PENDING with a pending workflow
 * request; approving moves it to PENDING_PAYMENT, rejecting to DECLINED,
 * cancelling to CANCELED; the payout job takes it through PROCESSING_PAYMENT to
 * COMPLETE, or to one of the two failure states.
 */
export const REIMBURSEMENT_TABS = {
  /** Still owed, the same list the dashboard's Reimbursements row counts. */
  OUTSTANDING: {
    key: "outstanding",
    label: "Outstanding",
    status: OUTSTANDING_REIMBURSEMENT_STATUSES,
  },
  ALL: { key: "all", label: "All", status: null },
} as const;

/** Only the fields the queue reads off the shared `transactions` query. */
export interface ReimbursementNode {
  id: string;
  type?: string | null;
  memo?: string | null;
  amount: number;
  currency?: string | null;
  transactionDate: string | number;
  status?: string | null;
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    profilePicUrl?: string | null;
  } | null;
  workflowRequest?: { status?: WorkflowRequestStatus | null } | null;
}

export interface ReimbursementRow {
  id: string;
  type: TransactionsType;
  userName: string;
  user?: UserAvatarUser | null;
  description: string;
  date: Date;
  amount: number;
  currency?: string | null;
  status: string;
  workflowStatus?: WorkflowRequestStatus;
}

/**
 * The amount keeps the sign the ledger stored it with (`-1 * amount`), so the
 * queue and the review drawer read the same figure for the same record.
 */
export const mapReimbursementNode = (
  node: ReimbursementNode,
): ReimbursementRow => ({
  id: node.id,
  type: node.type as TransactionsType,
  userName: fullName(node.user),
  user: node.user,
  description: node.memo ?? "",
  date: new Date(node.transactionDate),
  amount: node.amount,
  currency: node.currency,
  status: node.status ?? "",
  workflowStatus: node.workflowRequest?.status ?? undefined,
});
